import React, { useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { Button } from 'reactstrap';

import { connect } from 'react-redux';

import { toastr } from 'react-redux-toastr';

import { getPrivateService, postManualVoucher } from '../../../../actions';

import {
  CURRENCY_CODE,
  MANUAL_VOUCHER_PAYMENT_TYPE,
  MANUAL_VOUCHER_SERVICE_TYPE,
} from '../../../../config/constants';

import { currencyToNumber, numberFormatter } from '../../../../utils/number';

import { getSubstring } from '../../../../utils/string';

import { tzNormalizeDate } from '../../../../utils/date';

import Modal from '../../../common/modal/Modal';

import ManualVoucherForm from '../../accounting/manual-voucher/ManualVoucherForm';

const EMPTY_VOUCHER_TYPES = [];

const PrivateServiceManualVoucherButton = ({
  contract,
  dispatchPostManualVoucher,
  dispatchGetPrivateService,
}) => {
  const [showModal, setShowModal] = useState(false);

  const initialValues = useMemo(() => {
    if (!contract) {
      return null;
    }

    const {
      business,
      customer,
      source,
      destination,
      amountBeforeDiscount,
      totalAmount,
      creditPayment,
      serviceConcept,
      billingDescription,
    } = contract;

    const amountWithTaxes = Number(
      amountBeforeDiscount !== null && amountBeforeDiscount !== undefined
        ? amountBeforeDiscount
        : totalAmount || 0,
    );

    const amount = Math.round((amountWithTaxes / 1.18) * 100) / 100;

    const today = tzNormalizeDate();

    const businessId = business
      ? {
          value: business.id,
          label: `${business.businessTaxId} - ${business.name}`,
          businessTaxId: business.businessTaxId,
          name: business.name,
          email: business.email,
          primaryContact: business.primaryContact,
          phone: business.phone,
          fax: business.fax,
          addressSummary: business.addressSummary,
          addressId: business.addressId,
          address: business.address,
        }
      : null;

    const customerId = customer
      ? {
          value: customer.id,
          label:
            `${customer.idDocumentNumber} ` +
            `(${customer.idCountryOfOrigin}) - ` +
            `${customer.fullName}`,
          id: customer.id,
          firstName: customer.firstName,
          lastName: customer.lastName,
          fullName: customer.fullName,
          idDocumentNumber: customer.idDocumentNumber,
          gender: customer.gender,
          idCountryOfOrigin: customer.idCountryOfOrigin,
          identificationType: customer.identificationType,
          email: customer.email,
          mobilePhone: customer.mobilePhone,
          dob: customer.dob,
          discountCode: customer.discountCode,
        }
      : null;

    const paymentType = creditPayment
      ? MANUAL_VOUCHER_PAYMENT_TYPE.CREDIT
      : MANUAL_VOUCHER_PAYMENT_TYPE.CASH;

    const description =
      billingDescription ||
      serviceConcept ||
      `Servicio de transporte de personal de ${source || '-'} a ${
        destination || '-'
      }`;

    return {
      previousManualVoucherId: null,

      isPerson: !!customer,

      businessId,

      customerId,

      voucherTypeId: null,

      serviceType: MANUAL_VOUCHER_SERVICE_TYPE.CONTRACT,

      documentSeries: null,

      documentCode: null,

      issueDate: today,

      affectedByTaxes: true,

      currencyCode: CURRENCY_CODE.PEN,

      paymentType,

      installments: creditPayment ? 1 : null,

      dueDate: today,

      items: [
        {
          quantity: 1,

          description,

          unitaryPrice: amount,

          totalPrice: numberFormatter({
            style: 'currency',
            value: amount,
            currency: CURRENCY_CODE.PEN.value,
          }),
        },
      ],

      totalAmountWithoutTaxes: numberFormatter({
        style: 'currency',
        value: amount,
        currency: CURRENCY_CODE.PEN.value,
      }),

      taxes: numberFormatter({
        style: 'currency',
        value: 0,
        currency: CURRENCY_CODE.PEN.value,
      }),

      totalAmount: numberFormatter({
        style: 'currency',
        value: amount,
        currency: CURRENCY_CODE.PEN.value,
      }),

      totalAmountPayable: numberFormatter({
        style: 'currency',
        value: amount,
        currency: CURRENCY_CODE.PEN.value,
      }),

      detraction: null,

      detractionAmount: null,

      description: null,

      contractItineraryListElements: 'true',
    };
  }, [contract]);

  const onSubmit = async (formValues) => {
    const newFormValues = {
      ...formValues,
    };

    if (formValues.businessId) {
      newFormValues.businessId = formValues.businessId.value;
    }

    if (formValues.customerId) {
      newFormValues.customerId = formValues.customerId.value;
    }

    newFormValues.installments = formValues.installments || 1;
    newFormValues.voucherTypeId = formValues.voucherTypeId.value;
    newFormValues.serviceType = formValues.serviceType.value;
    newFormValues.currencyCode = formValues.currencyCode.value;
    newFormValues.paymentType = formValues.paymentType.value;

    newFormValues.items = formValues.items.map((item) => ({
      ...item,
      totalPrice: currencyToNumber(item.totalPrice),
    }));

    newFormValues.totalAmountWithoutTaxes = currencyToNumber(
      formValues.totalAmountWithoutTaxes,
    );

    newFormValues.exchangeRate = formValues.currencyRate
      ? currencyToNumber(formValues.currencyRate)
      : null;

    newFormValues.taxes = currencyToNumber(formValues.taxes);
    newFormValues.totalAmount = currencyToNumber(formValues.totalAmount);

    newFormValues.detractionAmount = formValues.detractionAmount
      ? currencyToNumber(formValues.detractionAmount)
      : null;

    newFormValues.totalAmountPayable = formValues.totalAmountPayable
      ? currencyToNumber(formValues.totalAmountPayable)
      : null;

    newFormValues.previousManualVoucherId = getSubstring(
      formValues.previousManualVoucherId,
    );

    newFormValues.contractId = contract.id;

    newFormValues.shouldRedirect = false;

    const response = await dispatchPostManualVoucher(newFormValues);

    if (response) {
      setShowModal(false);

      toastr.success(
        'Comprobante generado',
        'El comprobante fue generado correctamente.',
      );

      await dispatchGetPrivateService({ privateServiceId: contract.id });
    }

    return response;
  };

  const onShowModal = () => {
    setShowModal(true);
  };

  const onCancel = () => {
    setShowModal(false);
  };

  return (
    <>
      <Button color="primary" onClick={onShowModal}>
        <i className="fa fa-file-text-o" /> Generar Comprobante
      </Button>

      <Modal
        show={showModal}
        title="Generar Comprobante Manual"
        onClickClose={onCancel}
        onClickCancel={onCancel}
        body={
          initialValues ? (
            <ManualVoucherForm
              onSubmit={onSubmit}
              initialValues={initialValues}
              showPrivateServiceSearch={false}
            />
          ) : null
        }
        size="xl"
      />
    </>
  );
};

PrivateServiceManualVoucherButton.propTypes = {
  contract: PropTypes.shape({
    id: PropTypes.number.isRequired,
    contractPaymentType: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    amountBeforeDiscount: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    totalAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    creditPayment: PropTypes.bool,
    source: PropTypes.string,
    destination: PropTypes.string,
    serviceConcept: PropTypes.string,
    billingDescription: PropTypes.string,
    business: PropTypes.instanceOf(Object),
    customer: PropTypes.instanceOf(Object),
  }).isRequired,

  dispatchPostManualVoucher: PropTypes.func.isRequired,
  dispatchGetPrivateService: PropTypes.func.isRequired,
};

const mapStateToProps = ({ AccountingUnit }) => ({
  voucherTypes:
    AccountingUnit.VoucherType.getIn(['all', 'content', 'content']) ||
    EMPTY_VOUCHER_TYPES,
});

const mapDispatchToProps = {
  dispatchPostManualVoucher: postManualVoucher,
  dispatchGetPrivateService: getPrivateService,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PrivateServiceManualVoucherButton);
